import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { RadioButtonAllModule } from '@syncfusion/ej2-angular-buttons';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { TranslationService } from '../../../src/app/service/translation.service';
import { MessageLinesPipe } from './message-lines.pipe';

/**
 * Displays an alert popup dialog with optional confirmation, language switching, and navigation actions.
 * @component
 * @selector app-alert-popup
 * @example
 *   <app-alert-popup [Data]="sample_value1" (close)="handler()"></app-alert-popup>
 * @input { { alertMessage: string; alertVisible: string } } Data - Alert content and visibility state for the popup
 * @input {string} Message - Message text displayed in the popup
 * @input {string} isSuccess - Indicates whether the alert represents a success state
 * @input {number} Width - Width of the dialog in pixels
 * @input {number} Height - Height of the dialog in pixels
 * @input {number} id - Identifier emitted with confirm actions
 * @output {void} close - Emitted when the popup is closed
 * @output {number} confirm - Emitted when the user confirms the action
 * @method onConfirm Emits the current id through the confirm event
 * @method closePopup Resets popup state and emits close
 * @method switchLanguage Switches the application language using the selected language
 * @method onLanguageChange Updates the selected language and clears validation messages
 * @method onChangeNowClick Navigates to the change password route
 * @method onExtendNowClick Emits the confirm event
 * @method onCloseClick Emits the close event
 * @lifecycle ngAfterViewInit Makes the dialog visible after the view initializes
 */
@Component({
  selector: 'app-alert-popup',
  standalone: true,
  imports: [DialogModule, TranslateModule, CommonModule, FormsModule, RadioButtonAllModule, MessageLinesPipe],
  templateUrl: './alert-popup.component.html',
  styleUrl: './alert-popup.component.css',
})
export class AlertPopupComponent {
  @Input() Data = { alertMessage: '', alertVisible: '' }
  @Input() disclosureInformation: any = { title: '', description: '', visible: false };
  @Input() Message: string = '';
  @Input() isSuccess: string = '';
  @Output() close = new EventEmitter<void>();
  @Input() Width = 300;
  @Input() Height = 250;
  @Input() id = 0;
  currentLanguage = 'en';
  successMessage: string = '';
  errorMessage: string = '';
  @Output() confirm = new EventEmitter<number>();
  @Output() yesDisclosureEvent = new EventEmitter<string>();
  @Output() noDisclosureEvent = new EventEmitter<string>();
  @ViewChild('dialog') dialog: any;
  public selectedLanguage: string = '';
  public showValidationMsg: boolean = false;

  constructor(private translationService: TranslationService, private router: Router) { }

  /**
   * `trackBy` for the error dialog's message lines.
   *
   * The lines come from the pure {@link MessageLinesPipe}, so the array identity is already
   * stable between change-detection passes and Angular re-diffs the list only when the message
   * itself changes. Tracking by position keeps the `<span>` nodes across that diff too, so a
   * message that gains or loses a line updates the text in place rather than rebuilding the
   * lines that did not move.
   *
   * @param index - Position of the line within the split message.
   * @returns The index itself, used as the line's identity.
   */
  trackByLineIndex(index: number): number {
    return index;
  }

  onConfirm() {
    this.confirm.emit(this.id);
  }
  ngAfterViewInit() {
    if (this.dialog) {
      this.dialog.visible = true;
    }
  }

  closePopup() {
    this.disclosureInformation.visible = false;
    this.disclosureInformation.title = '';
    this.disclosureInformation.description = '';
    this.isSuccess = '';
    this.Data.alertVisible = '';
    this.Data.alertMessage = '';
    this.close.emit();
  }

  switchLanguage() {
    if (!this.selectedLanguage) {
      this.showValidationMsg = true;
    } else {
      this.showValidationMsg = false;

      this.translationService.switchLanguage(this.selectedLanguage);
      this.closePopup();
    }
  }

  onLanguageChange(value: string) {
    this.selectedLanguage = value;
    this.showValidationMsg = false;
  }

  /**
   * Languages offered in the "Switch Language" dialog. `labelKey` resolves through
   * `@ngx-translate` to the language's endonym — the language in its own script with the
   * English name in parentheses. Those values are held IDENTICAL in every
   * `assets/i18n/*.json` file (matching the pre-existing `layout.language.*` convention):
   * a language picker must render every option in its own script whatever the active UI
   * language, or a speaker cannot find their language from a UI they cannot read.
   * Do not localize them. Kept in sync with `LayoutComponent.languages`.
   */
  public languages = [
    { labelKey: 'layout.language.english', value: 'en' },
    { labelKey: 'layout.language.bengali', value: 'bn' },
    { labelKey: 'layout.language.gujarati', value: 'gu' },
    { labelKey: 'layout.language.hindi', value: 'hi' },
    { labelKey: 'layout.language.kannada', value: 'kn' },
    { labelKey: 'layout.language.malayalam', value: 'ml' },
    { labelKey: 'layout.language.marathi', value: 'mr' },
    { labelKey: 'layout.language.odia', value: 'or' },
    { labelKey: 'layout.language.punjabi', value: 'pa' },
    { labelKey: 'layout.language.tamil', value: 'ta' },
    { labelKey: 'layout.language.telugu', value: 'te' }
  ];

  onChangeNowClick() {
    this.router.navigate(['/changePassword']);
  }

  onExtendNowClick() {
    this.confirm.emit();
  }

  onCloseClick() {
    this.close.emit();
  }

  onConfirmYesDisclosure() {
    this.disclosureInformation.visible = false;
    this.disclosureInformation.title = '';
    this.disclosureInformation.description = '';
    this.Data.alertVisible = '';
    this.Data.alertMessage = '';
    this.yesDisclosureEvent.emit()
  }

  onConfirmNoDisclosure() {
    this.disclosureInformation.visible = false;
    this.disclosureInformation.title = '';
    this.disclosureInformation.description = '';
    this.Data.alertVisible = '';
    this.Data.alertMessage = '';
    this.noDisclosureEvent.emit()
  }
}